<template>
  <div class="d-flex align-items-center">
    <button
      v-if="!isLoggedIn"
      class="btn btn-sm btn-link text-muted p-1 mr-2 text-decoration-none guest-theme-btn"
      :title="themeLabel"
      @click="toggleTheme"
    >
      <span style="font-size: 14px;">{{ theme === 'dark' ? '☀️' : '🌙' }}</span>
    </button>
    <b-nav-item v-if="!isLoggedIn" @click="goLogin">
      登录/注册
    </b-nav-item>
    <template v-else>
      <!-- 站内通知滚动提示条（有未读消息时展示） -->
      <div 
        v-if="unreadNotificationsCount > 0"
        class="notify-ticker-pill d-none d-md-flex align-items-center mr-2"
        :class="{ 'has-multiple': unreadTickerList.length > 1 }"
        :title="currentTickerTooltip"
        @click="openNotificationModal"
        @mouseenter="pauseTicker"
        @mouseleave="resumeTicker"
      >
        <span class="ticker-horn-icon mr-1 flex-shrink-0">
          <b-icon icon="volume-up-fill" class="horn-anim"></b-icon>
        </span>

        <div class="ticker-viewport">
          <transition name="ticker-slide" mode="out-in">
            <div :key="currentTickerKey" class="ticker-slide-item d-flex align-items-center">
              <span 
                v-if="currentTickerTypeBadge" 
                class="badge badge-pill mr-1 flex-shrink-0 ticker-type-badge" 
                :class="currentTickerTypeBadge.badgeClass"
              >
                {{ currentTickerTypeBadge.label }}
              </span>
              <span class="ticker-text text-truncate">{{ currentTickerTitle }}</span>
            </div>
          </transition>
        </div>

        <span class="ticker-arrow ml-1 flex-shrink-0">
          <b-icon icon="chevron-right" font-scale="0.75"></b-icon>
        </span>
      </div>

      <!-- 站内通知铃铛 -->
      <b-nav-item 
        class="notification-bell-btn mr-1" 
        @click="openNotificationModal"
        title="站内通知"
      >
        <span class="position-relative d-inline-block">
          <b-icon icon="bell" font-scale="1.15"></b-icon>
          <span 
            v-if="unreadNotificationsCount > 0" 
            class="badge badge-pill badge-danger notification-badge"
          >
            {{ unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount }}
          </span>
        </span>
      </b-nav-item>

      <b-nav-item-dropdown right :dropup="isMobile">
        <template #button-content>
          <span v-if="avatarUrl" class="d-inline-block mr-2" style="width:24px;height:24px;border-radius:50%;overflow:hidden;vertical-align:middle;">
            <img :src="avatarUrl" style="width:24px;height:24px;object-fit:cover;" />
          </span>
          <span v-else class="avatar-fallback-mini mr-2">{{ firstLetter }}</span>
          <span class="user-display-name">{{ displayName }}</span>
        </template>
        <b-dropdown-item @click="openNotificationModal">
          <b-icon icon="bell" class="mr-2"></b-icon>
          站内通知
          <b-badge v-if="unreadNotificationsCount > 0" variant="danger" pill class="ml-1">
            {{ unreadNotificationsCount }}
          </b-badge>
        </b-dropdown-item>
        <b-dropdown-item @click="openApkDownloadModal">
          <b-icon icon="phone" class="mr-2"></b-icon>App下载
        </b-dropdown-item>
        <b-dropdown-item @click="toggleTheme">{{ themeLabel }}</b-dropdown-item>
        <b-dropdown-item @click="toggleColorblind">{{ colorblindLabel }}</b-dropdown-item>
        <b-dropdown-item @click="goApiUsage">
          <b-icon icon="graph-up" class="mr-2"></b-icon>用量报表
        </b-dropdown-item>
        <b-dropdown-item @click="goProfile">设置</b-dropdown-item>
        <b-dropdown-divider></b-dropdown-divider>
        <b-dropdown-item @click="logout">退出登录</b-dropdown-item>
      </b-nav-item-dropdown>

      <!-- 站内通知弹窗组件 -->
      <NotificationModal ref="notifyModal" @unread-changed="onUnreadCountChanged" />
      <!-- APK下载弹窗组件 -->
      <ApkDownloadModal ref="apkDownloadModal" />
    </template>
  </div>
</template>

<script>
import { request_zzquant } from '@/network/request.js'
import NotificationModal from '@/components/notification/NotificationModal.vue'
import ApkDownloadModal from '@/components/download/ApkDownloadModal.vue'

export default {
  name: 'UserMenu',
  components: {
    NotificationModal,
    ApkDownloadModal,
  },
  data() {
    return {
      isLoggedIn: false,
      userInfo: null,
      loginHandlerRef: null,
      logoutHandlerRef: null,
      theme: 'light',
      colorblind: false,
      isMobile: false,
      unreadNotificationsCount: 0,
      unreadTickerList: [],
      tickerIndex: 0,
      tickerTimer: null,
      isTickerPaused: false,
    }
  },
  computed: {
    displayName() {
      const u = this.userInfo || {}
      return u.user_nick || u.user_name || u.email || '账户'
    },
    avatarUrl(){
      const u = this.userInfo || {}
      const url = u.avatar_url || u.avatar
      if (!url) return ''
      return url.startsWith('http') ? url : ('https://api.zizizaizai.com' + url)
    },
    firstLetter(){
      const name = this.displayName || ''
      return name ? name.substr(0,1) : '?'
    },
    themeLabel(){ return this.theme === 'dark' ? '浅色皮肤' : '黑色皮肤' },
    colorblindLabel(){ return this.colorblind ? '关闭色弱模式' : '开启色弱模式' },
    currentTickerItem() {
      if (this.unreadTickerList && this.unreadTickerList.length > 0) {
        return this.unreadTickerList[this.tickerIndex % this.unreadTickerList.length]
      }
      return null
    },
    currentTickerKey() {
      if (this.currentTickerItem && this.currentTickerItem.id) {
        return `item-${this.currentTickerItem.id}`
      }
      return `count-${this.unreadNotificationsCount}`
    },
    currentTickerTitle() {
      if (this.currentTickerItem && this.currentTickerItem.title) {
        return this.currentTickerItem.title
      }
      if (this.unreadNotificationsCount > 0) {
        return `您有 ${this.unreadNotificationsCount} 条未读通知`
      }
      return ''
    },
    currentTickerTypeBadge() {
      if (!this.currentTickerItem || !this.currentTickerItem.type) return null
      const map = {
        vip: { label: 'VIP', badgeClass: 'badge-warning text-dark font-weight-bold' },
        system: { label: '系统', badgeClass: 'badge-primary' },
        activity: { label: '活动', badgeClass: 'badge-success' },
        topic: { label: '题材', badgeClass: 'badge-info' }
      }
      return map[this.currentTickerItem.type] || null
    },
    currentTickerTooltip() {
      const title = this.currentTickerTitle
      const badge = this.currentTickerTypeBadge ? `[${this.currentTickerTypeBadge.label}] ` : ''
      return `${badge}${title}（点击查看）`
    }
  },
  mounted() {
    this.readLocal()
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
    try{ const t = localStorage.getItem('site.theme'); if(t){ this.theme = t } }catch(e){ console.warn('theme read failed', e) }
    try{ const c = localStorage.getItem('site.colorblind'); if(c){ this.colorblind = (c==='1' || c==='true') } }catch(e){ console.warn('colorblind read failed', e) }
    this.loginHandlerRef = (evt) => {
      try {
        if (evt && evt.detail && evt.detail.userInfo) {
          this.userInfo = evt.detail.userInfo
        }
      } catch (e) { console.warn('user-login event parse failed', e) }
      this.isLoggedIn = !!localStorage.getItem('token')
    }
    this.logoutHandlerRef = () => {
      this.isLoggedIn = false
      this.userInfo = null
      this.unreadNotificationsCount = 0
      this.unreadTickerList = []
      this.stopTickerTimer()
    }
    window.addEventListener('user-login', this.loginHandlerRef)
    window.addEventListener('user-logout', this.logoutHandlerRef)
    if (this.isLoggedIn) {
      this.loadProfile()
      try {
        const cached = localStorage.getItem('site.notify.unread_count')
        if (cached !== null) {
          const count = parseInt(cached, 10) || 0
          this.unreadNotificationsCount = count
          if (count > 0) {
            this.fetchUnreadTickerList()
          }
        }
      } catch (e) {
        console.warn('read notify cache failed', e)
      }
    }
  },
  beforeDestroy() {
    this.stopTickerTimer()
    window.removeEventListener('resize', this.checkMobile)
    window.removeEventListener('user-login', this.loginHandlerRef)
    window.removeEventListener('user-logout', this.logoutHandlerRef)
  },
  methods: {
    readLocal() {
      try {
        this.isLoggedIn = !!localStorage.getItem('token')
        const raw = localStorage.getItem('user_info')
        if (raw) this.userInfo = JSON.parse(raw)
      } catch (e) {
        this.isLoggedIn = !!localStorage.getItem('token')
        this.userInfo = null
        console.warn('readLocal user_info failed', e)
      }
    },
    checkMobile() {
      this.isMobile = window.innerWidth <= 991
    },
    goLogin() {
      try {
        const current = this.$router.currentRoute && this.$router.currentRoute.fullPath ? this.$router.currentRoute.fullPath : '/'
        this.$router.push({ path: '/login', query: { redirect: current } })
      } catch (e) {
        this.$router.push('/login')
      }
    },
    goProfile() {
      if (!this.$route || this.$route.path !== '/me/profile') {
        this.$router.push('/me/profile').catch(() => {})
      }
    },
    goApiUsage() {
      if (!this.$route || this.$route.path !== '/me/profile' || this.$route.query.tab !== 'usage') {
        this.$router.push('/me/profile?tab=usage').catch(() => {})
      }
    },
    goHome() {
      if (!this.$route || this.$route.path !== '/me/home') {
        this.$router.push('/me/home').catch(() => {})
      }
    },
    applyTheme(){ const isDark = this.theme === 'dark'; document.body.classList.toggle('theme-dark', isDark); try{ localStorage.setItem('site.theme', this.theme); window.dispatchEvent(new CustomEvent('site-theme-changed', { detail:{ theme:this.theme } })) }catch(e){ console.warn('theme set failed', e) } },
    toggleTheme(){ this.theme = (this.theme==='dark') ? 'light' : 'dark'; this.applyTheme() },
    applyColorblind(){ try{ localStorage.setItem('site.colorblind', this.colorblind ? '1' : '0'); window.dispatchEvent(new CustomEvent('site-colorblind-changed', { detail:{ colorblind: !!this.colorblind } })) }catch(e){ console.warn('colorblind set failed', e) } },
    toggleColorblind(){ this.colorblind = !this.colorblind; this.applyColorblind() },
    loadProfile(){
      request_zzquant({ url: '/v3/user/profile/me', method: 'GET' }).then(res => {
        const d = res.data && res.data.data ? res.data.data : {}
        const info = Object.assign({}, this.userInfo || {}, { id: d.id, user_name: d.user_name, user_nick: d.user_nick, avatar_url: d.avatar_url })
        if (d.roles) {
          info.roles = d.roles
        }
        this.userInfo = info
        try { 
          localStorage.setItem('user_info', JSON.stringify(info))
          window.dispatchEvent(new CustomEvent('user-profile-updated', { detail: info }))
        } catch(e) { console.warn('persist user_info failed', e) }
      }).catch(()=>{})
    },
    async logout() {
      const current = this.$router.currentRoute && this.$router.currentRoute.fullPath ? this.$router.currentRoute.fullPath : '/'
      try {
        await request_zzquant({ url: '/v3/auth/logout', method: 'post' })
      } catch (e) {
        console.warn('logout api failed', e)
      } finally {
        try {
          localStorage.removeItem('token')
          localStorage.removeItem('refresh_token')
          localStorage.removeItem('user_info')
        } catch (e) { console.warn('clear local auth failed', e) }
        window.dispatchEvent(new CustomEvent('user-logout'))
        this.$router.push({ path: '/login', query: { redirect: current } })
      }
    },
    openNotificationModal() {
      if (this.$refs.notifyModal) {
        this.$refs.notifyModal.open()
      }
    },
    openApkDownloadModal() {
      if (this.$refs.apkDownloadModal) {
        this.$refs.apkDownloadModal.open()
      } else {
        this.$router.push('/download')
      }
    },
    onUnreadCountChanged(count) {
      const prevCount = this.unreadNotificationsCount
      this.unreadNotificationsCount = count
      if (count <= 0) {
        this.unreadTickerList = []
        this.stopTickerTimer()
      } else {
        if (this.unreadTickerList.length === 0 || count !== prevCount) {
          this.fetchUnreadTickerList()
        }
      }
    },
    fetchUnreadTickerList() {
      const token = localStorage.getItem('token')
      if (!token || !this.isLoggedIn) {
        this.unreadTickerList = []
        this.stopTickerTimer()
        return
      }
      request_zzquant({
        url: '/v3/notifications',
        method: 'GET',
        params: {
          page: 1,
          page_size: 5,
          is_read: 0,
        },
        noRedirect: true,
      }).then(res => {
        if (res && res.data && res.data.code === 20000) {
          const data = res.data.data || {}
          const items = data.items || []
          this.unreadTickerList = items
          if (items.length > 0) {
            if (this.tickerIndex >= items.length) {
              this.tickerIndex = 0
            }
            this.startTickerTimer()
          } else {
            this.stopTickerTimer()
          }
        }
      }).catch(err => {
        console.warn('fetchUnreadTickerList failed', err)
      })
    },
    startTickerTimer() {
      this.stopTickerTimer()
      if (this.unreadTickerList.length <= 1) {
        return
      }
      this.tickerTimer = setInterval(() => {
        if (!this.isTickerPaused && this.unreadTickerList.length > 1) {
          this.tickerIndex = (this.tickerIndex + 1) % this.unreadTickerList.length
        }
      }, 3500)
    },
    stopTickerTimer() {
      if (this.tickerTimer) {
        clearInterval(this.tickerTimer)
        this.tickerTimer = null
      }
    },
    pauseTicker() {
      this.isTickerPaused = true
    },
    resumeTicker() {
      this.isTickerPaused = false
    }
  }
}
</script>

<style scoped>
.avatar-fallback-mini {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: var(--accent-color, #f0b90b);
  color: #fff;
  font-size: 12px;
  font-weight: bold;
  vertical-align: middle;
}

.user-display-name {
  vertical-align: middle;
}

.notification-badge {
  position: absolute;
  top: -6px;
  right: -10px;
  font-size: 0.65rem;
  padding: 0.2em 0.45em;
  line-height: 1;
}

.notification-bell-btn >>> .nav-link {
  padding-left: 0.5rem !important;
  padding-right: 0.5rem !important;
  color: rgba(255, 255, 255, 0.75) !important;
  transition: color 0.2s;
  cursor: pointer;
}

.notification-bell-btn:hover >>> .nav-link {
  color: #ffffff !important;
}

/* 站内通知滚动提示条样式 */
.notify-ticker-pill {
  height: 30px;
  line-height: 1;
  padding: 0 10px 0 8px;
  border-radius: 15px;
  background-color: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.35);
  color: #ff4d4f;
  cursor: pointer;
  user-select: none;
  transition: all 0.25s ease;
  font-size: 12px;
  max-width: 240px;
}

body.theme-dark .notify-ticker-pill {
  background-color: rgba(239, 68, 68, 0.18);
  border: 1px solid rgba(248, 113, 113, 0.45);
  color: #fca5a5;
}

.notify-ticker-pill:hover {
  background-color: rgba(239, 68, 68, 0.22);
  border-color: rgba(239, 68, 68, 0.7);
  box-shadow: 0 0 8px rgba(239, 68, 68, 0.25);
}

body.theme-dark .notify-ticker-pill:hover {
  background-color: rgba(239, 68, 68, 0.3);
  border-color: #f87171;
  box-shadow: 0 0 10px rgba(248, 113, 113, 0.35);
}

.ticker-horn-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ff4d4f;
  font-size: 13px;
}

body.theme-dark .ticker-horn-icon {
  color: #f87171;
}

.horn-anim {
  animation: ticker-horn-shake 2.4s infinite ease-in-out;
  transform-origin: center;
}

@keyframes ticker-horn-shake {
  0%, 75%, 100% {
    transform: rotate(0deg) scale(1);
  }
  80% {
    transform: rotate(-12deg) scale(1.15);
  }
  85% {
    transform: rotate(10deg) scale(1.15);
  }
  90% {
    transform: rotate(-8deg) scale(1.15);
  }
  95% {
    transform: rotate(4deg) scale(1.05);
  }
}

.ticker-viewport {
  height: 20px;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.ticker-slide-item {
  width: 100%;
  height: 20px;
  line-height: 20px;
  white-space: nowrap;
}

.ticker-type-badge {
  font-size: 10px !important;
  padding: 1px 4px !important;
  line-height: 1.2 !important;
  font-weight: 600;
  border-radius: 3px;
}

.ticker-text {
  font-weight: 500;
  letter-spacing: 0.2px;
  color: inherit;
}

.ticker-arrow {
  display: inline-flex;
  align-items: center;
  opacity: 0.65;
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.notify-ticker-pill:hover .ticker-arrow {
  opacity: 1;
  transform: translateX(2px);
}

/* 垂直滑动切换动画 */
.ticker-slide-enter-active,
.ticker-slide-leave-active {
  transition: all 0.32s cubic-bezier(0.25, 1, 0.5, 1);
}

.ticker-slide-enter {
  opacity: 0;
  transform: translateY(100%);
}

.ticker-slide-leave-to {
  opacity: 0;
  transform: translateY(-100%);
}
</style>
